import { ArrowRightOutlined, LoadingOutlined, MailOutlined } from "@ant-design/icons";
import { Button, Card, Col, Empty, Form, Input, Layout, message, Result, Row, Spin, Typography } from "antd";
import { observer } from "mobx-react-lite";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { FloatingWhatsApp } from 'react-floating-whatsapp';
import supportAvatar from '../assets/support.jpg';
import { GlobalStore } from "../store/store";
import { useTranslation } from "react-i18next";
import { useAppLanguage } from "../hooks/useAppLanguage";

const { Content } = Layout;

interface ICheckoutPageProps {
  store: GlobalStore;
};

export const CheckoutPage: FC<ICheckoutPageProps> = observer(({ store }) => {
  const { t } = useTranslation();
  const { language } = useAppLanguage();
  const [productID, setProductID] = useState<string | null>(null);
  const [intentID, setIntentID] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState<boolean | null>(null);

  useEffect(() => {
    if (store.products.length === 0) {
      store.getProducts();
    }

    // Check to see if this is a redirect back from Checkout
    const query = new URLSearchParams(window.location.search);

    if (query.get("success")) {
      setSuccess(true);
    }

    if (query.get("canceled")) {
      message.warning(t('checkout.canceled'));
      setSuccess(false);
    }

    if (query.get("product_id")) {
      setProductID(query.get("product_id"));
    }

    if (query.get("intent_id")) {
      setIntentID(query.get("intent_id"));
    }

    setLoading(false);
  }, [store, t]);

  const product = useMemo(() => {
    if (!productID || store.products.length === 0) { return; }

    return store.products.find(p => p.ProductID === productID);
  }, [productID, store.products])

  return (
    <Layout>
      <Content style={{ padding: '0 50px 50px 50px', width: 1150, margin: "auto" }}>
        <div style={{ padding: 50 }}>
          {success === true &&
            <GoFarmSuccess product={product} intent_id={intentID} loading={store.loading || loading} store={store} />
          }

          {success === false &&
            <Empty description={t('checkout.not_found')} />
          }
        </div>
      </Content>
    </Layout>
  );
}
)

type CanSetPasswordResponse = {
  Wait: boolean,
  Can: boolean,
  Blocked: boolean,
  Redir: boolean,
  Email: string,
  ProductSerial: string,
  Success: boolean,
}

const fetchCanSetPassword = async (intent_id: string, t: any) => {
  try {
    const params = new URLSearchParams({ intent_id });
    const can_set_resp = await fetch("/api/password/check?" + params.toString(), {
      method: 'GET',
    });

    const can_set = await can_set_resp.json() as CanSetPasswordResponse;
    return can_set;
  } catch (e) {
    console.error(e);
    message.error(t('checkout.error_loading_purchase'))
    return null;
  }
}

type SetPasswordRequest = {
  IntentID: string,
  Password: string,
}

type SetPasswordResponse = {
  Success: boolean,
}

const fetchSetPassword = async (body: SetPasswordRequest, t: any) => {
  try {
    const set_password_resp = await fetch("/api/password/set", {
      method: 'POST',
      body: JSON.stringify(body),
    });

    const set_password = await set_password_resp.json() as SetPasswordResponse;
    return set_password;
  } catch (e) {
    console.error(e);
    message.error(t('checkout.error_setting_password'))
    return null;
  }
}

type SuccessPageProps = {
  product: any,
  intent_id: string | null,
  loading: boolean,
  store: GlobalStore,
}

type SuccessPageState =
  { Loading: boolean, Creating: boolean } &
  CanSetPasswordResponse &
  SetPasswordResponse

type SynchronizeRequest = { IntentID: string };

const fetchSynchronize = async (body: SynchronizeRequest) => {
  try {
    await fetch("/api/synchronize", {
      method: 'POST',
      body: JSON.stringify(body),
    });

  } catch (e) {
    console.error(e);
    return null;
  }
}

const GoFarmSuccess = observer(({ product, intent_id, loading, store }: SuccessPageProps) => {
  const { t } = useTranslation();
  const { language } = useAppLanguage();
  const [success, setSuccess] = useState(false);
  const [state, setState] = useState<SuccessPageState>({
    Email: "",
    ProductSerial: '',
    Loading: false,
    Creating: false,
    Blocked: false,
    Can: false,
    Redir: false,
    Wait: false,
    Success: false,
  });

  const load = (intent_id: string) => {
    setState(state => ({ ...state, Loading: true }));

    fetchCanSetPassword(intent_id, t)
      .then((resp) => {
        setState(state => ({ ...state, ...resp, Loading: false }))
        setSuccess(resp?.Success ?? false);

        if (!resp?.Success && resp?.Wait) {
          fetchSynchronize({ IntentID: intent_id });
        }

        if ((!resp?.Can && !resp?.Redir && !resp?.Success) || resp?.Blocked) {
          if (timeout_id.current) {
            clearTimeout(timeout_id.current);
          }
        }
      });
  }

  useEffect(() => {
    if (!intent_id) { return; }

    load(intent_id);
  }, [intent_id]);

  const timeout_id = useRef<NodeJS.Timeout | null>(null)
  useEffect(() => {
    if ((intent_id && !state.Can && !state.Redir && !state.Blocked && !(state.Success || success))
      || (intent_id && state.Blocked && !state.ProductSerial)) {
      if (timeout_id.current) {
        clearTimeout(timeout_id.current);
      }
      timeout_id.current = setTimeout(() => load(intent_id), 10000);
    }
  }, [state, intent_id]);

  const formSubmit = async (v: any) => {
    const body: SetPasswordRequest = {
      IntentID: intent_id!,
      Password: v.password,
    };

    setState(state => ({ ...state, Creating: true }));
    const resp = await fetchSetPassword(body, t);
    setState(state => ({ ...state, ...resp, Creating: false }));
    setSuccess(resp?.Success ?? false);

    if (resp?.Success) {
      message.success(t('checkout.access_data_success_msg'))
    } else {
      message.error(t('checkout.error_registering_access'))
    }
  }

  const display_loading = loading || state.Wait || state.Loading;

  const sendEmail = () => {
    if (!intent_id) { return; }

    store.sendMail(intent_id)
  }

  const [wait_time, set_wait_time] = useState(0);
  useEffect(() => {
    if (store.send_state?.WaitTime && store.send_state.WaitTime > 0) {
      set_wait_time(store.send_state.WaitTime);
    }

    if (store.send_state?.Success) {
      message.success(t('checkout.email_sent_success'))
    }
  }, [store.send_state, t]);

  useEffect(() => {
    if (wait_time > 0) {
      setTimeout(() => set_wait_time(wait_time - 1), 1000);
    }
  }, [wait_time]);

  return (
    <div>
      <Row gutter={30}>
        <Col md={12} style={{ display: "flex", alignItems: "center" }}>
          <img src="img/gofarm.png" style={{ height: 360 }} alt="GoFarm Logo" />
        </Col>

        <Col md={12}>
          <Card>
            <Result
              status="success"
              title={`${product?.Data?.Name ?? "GoFarm®"} - ${t('checkout.success')} `}
            />

            {(display_loading && !(state.Can && !state.Success) && !state.Blocked) &&
              <div style={{ textAlign: "center", marginBottom: 48 }}>
                <Spin indicator={<LoadingOutlined />} />
              </div>}

            {(state.Can && !state.Success) &&
              <>
                <Typography style={{ color: "rgba(0, 0, 0, 0.6)", fontSize: 14 }}>
                  {t('checkout.access_data_instruction')}
                </Typography>

                <Form
                  layout="vertical"
                  onFinish={formSubmit}
                >
                  <Typography.Title level={5}>
                    {t('checkout.access_data_title')}
                  </Typography.Title>

                  <Form.Item label="E-Mail">
                    <Input disabled value={state.Email} />
                  </Form.Item>

                  <Form.Item
                    label={t('checkout.password')}
                    required
                    name="password"
                    rules={[
                      {
                        required: true,
                        message: t('checkout.password_required'),
                      },
                    ]}
                  >
                    <Input.Password />
                  </Form.Item>

                  <Form.Item
                    label={t('checkout.confirm_password')}
                    required
                    name="confirm_password"
                    rules={[
                      {
                        required: true,
                        message: t('checkout.confirm_password_required'),
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('password') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error(t('checkout.passwords_match_error')));
                        },
                      }),
                    ]}
                  >
                    <Input.Password />
                  </Form.Item>

                  <div style={{ height: 10 }} />

                  <Form.Item>
                    <Button type="primary" htmlType="submit" style={{ width: 150 }}>
                      {t('checkout.register_access')} {state.Creating && <Spin indicator={<LoadingOutlined style={{ color: "#fff" }} />} />}
                    </Button>
                  </Form.Item>
                </Form>
              </>}

            {state.ProductSerial &&
              <Typography style={{ color: "rgba(0, 0, 0, 0.6)", fontSize: 16, textAlign: 'center', marginBottom: 40, marginTop: -32 }}>
                {state.ProductSerial}
              </Typography>}

            {state.Success &&
              <>
                <Typography style={{ color: "rgba(0, 0, 0, 0.6)", fontSize: 16, marginBottom: 20 }}>
                  {t('checkout.access_data_success')}<br /><br />
                  {t('checkout.keep_password')}<br /><br />
                  {t('checkout.email_sent', { email: state.Email })}<br /><br />
                </Typography>

                <Typography style={{ color: "rgba(0, 0, 0, 0.88)", fontSize: 18, marginBottom: 40 }}>
                  {t('checkout.thank_you')}
                </Typography>
              </>}

            {(!state.Success && state.Blocked) &&
              <Typography style={{ color: "rgba(0, 0, 0, 0.6)", fontSize: 16, textAlign: 'center', marginBottom: 40 }}>
                {t('checkout.manager_panel_instruction')}<br />
                E-Mail: {state.Email}
              </Typography>}

            {(!state.Success && !state.Blocked && display_loading) &&
              <Typography style={{ color: "rgba(0, 0, 0, 0.6)", fontSize: 16, textAlign: 'center', marginBottom: 40 }}>
                {t('checkout.protocol')}: {intent_id}.<br />
                {t('checkout.confirmation_waiting')}
              </Typography>}

            {(state.Redir || state.Blocked) &&
              <div style={{ textAlign: "center", marginTop: -10, marginBottom: 40 }}>
                <Button type="primary" key="console" onClick={() => window.location.href = "https://secure.tecgraf.com.br/sess"}>
                  {t('checkout.access_panel')} <ArrowRightOutlined />
                </Button>
              </div>}

            {(state.Blocked || state.Success) &&
              <div style={{ textAlign: "center", marginTop: -10, marginBottom: 40 }}>
                <Button type="default" key="console" onClick={() => sendEmail()} disabled={store.sending || wait_time > 0}>
                  {store.sending && <Spin indicator={<LoadingOutlined style={{ fontSize: 14 }} />} />}
                  {!store.sending &&
                    (wait_time > 0) ?
                    <>
                      ({wait_time}) {t('checkout.wait_a_bit')} <MailOutlined />
                    </>
                    :
                    <>
                      {t('checkout.resend_email')} <MailOutlined />
                    </>
                  }
                </Button>
              </div>}
          </Card>
        </Col>
      </Row>

      <FloatingWhatsApp
        accountName="Suporte Tecgraf Agro"
        phoneNumber="+5519999174690"
        avatar={supportAvatar}
        statusMessage={t('common.support_status')}
        chatMessage={t('common.support_message')}
        placeholder={t('common.whatsapp_placeholder')}
      />
    </div>
  );
})
